import type { ChatMessageViewModel } from './chatViewModels';
import type { Conversation, Message, Paginator } from '@twilio/conversations';

import { useCallback, useEffect, useMemo, useState } from 'react';

import { useIsFocused } from '@react-navigation/native';
import { YStack } from 'tamagui';

import { Toast } from '@shared/utils';
import { useChatStore } from '@south-street-app/stores';
import { markConversationRead } from '@south-street-app/utils';
import { useGetCurrentEndUser } from '@utility-nyc/react-query-sdk';

import { ChatConversationPanel } from './ChatConversationPanel';
import {
  mergeChatMessages,
  toChatMessageViewModel,
  withResolvedChatMessage,
} from './chatViewModels';

const PAGE_SIZE = 30;

type ConversationChatViewProps = {
  conversation: Conversation | null;
  emptyMessage: string;
  hideSenderIdentity?: boolean;
  inlineTitle?: string;
  onSendMessage?: (message: string) => Promise<void>;
  placeholder?: string;
  readOnly?: boolean;
  receivedBubbleBackground?: 'default' | 'muted';
  avatarName?: string;
};

const ConversationChatView = ({
  conversation,
  emptyMessage,
  hideSenderIdentity = false,
  inlineTitle,
  onSendMessage,
  placeholder = 'Message',
  readOnly = false,
  receivedBubbleBackground = 'default',
  avatarName,
}: ConversationChatViewProps) => {
  const [messages, setMessages] = useState<ChatMessageViewModel[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingEarlier, setLoadingEarlier] = useState(false);
  const [sending, setSending] = useState(false);
  const [paginator, setPaginator] = useState<Paginator<Message> | null>(null);
  const isFocused = useIsFocused();
  const lockChat = useChatStore((state) => state.lockChat);
  const { data: currentUser } = useGetCurrentEndUser();

  const activeUserId = useMemo(
    () => `END_USER:${currentUser?.data.id ?? ''}`,
    [currentUser?.data.id],
  );
  const mapMessage = useCallback(
    (message: Message) =>
      toChatMessageViewModel({
        conversation,
        message,
      }),
    [conversation],
  );
  const presentedMessages = useMemo(
    () =>
      messages.map((message) => {
        if (message.side === 'system') {
          return message;
        }

        const side: ChatMessageViewModel['side'] =
          message.authorId === activeUserId ? 'right' : 'left';

        return {
          ...message,
          side,
          avatarLabel: side === 'left' ? avatarName : undefined,
        };
      }),
    [activeUserId, avatarName, messages],
  );
  const displayMessages = useMemo(
    () =>
      withResolvedChatMessage({ conversation, messages: presentedMessages }),
    [conversation, presentedMessages],
  );

  useEffect(() => {
    let isMounted = true;

    const loadMessages = async () => {
      if (!conversation) {
        setMessages([]);

        setPaginator(null);

        setLoading(false);

        return;
      }

      setLoading(true);

      try {
        const nextPaginator = await conversation.getMessages(PAGE_SIZE);

        if (!isMounted) {
          return;
        }

        setPaginator(nextPaginator);

        setMessages(
          mergeChatMessages(nextPaginator.items)
            .map(mapMessage)
            .filter((message): message is ChatMessageViewModel =>
              Boolean(message),
            ),
        );
      } catch {
        if (isMounted) {
          setMessages([]);

          setPaginator(null);

          Toast.showErrorToast({
            text1: 'Could not load messages',
            text2: 'Please try again.',
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadMessages().catch(() => undefined);

    return () => {
      isMounted = false;
    };
  }, [conversation, mapMessage]);

  useEffect(() => {
    if (!isFocused) {
      return;
    }

    markConversationRead(conversation).catch(() => undefined);
  }, [conversation, isFocused]);

  useEffect(() => {
    if (!conversation) {
      return;
    }

    const handleMessageAdded = (message: Message) => {
      const nextMessage = mapMessage(message);

      if (!nextMessage) {
        return;
      }

      setMessages((currentMessages) => {
        if (
          currentMessages.some(
            (currentMessage) => currentMessage.id === nextMessage.id,
          )
        ) {
          return currentMessages;
        }

        return mergeChatMessages([nextMessage, ...currentMessages]);
      });

      if (isFocused) {
        markConversationRead(conversation).catch(() => undefined);
      }
    };

    conversation.on('messageAdded', handleMessageAdded);

    return () => {
      conversation.removeListener('messageAdded', handleMessageAdded);
    };
  }, [conversation, isFocused, mapMessage]);

  const onLoadEarlier = useCallback(async () => {
    if (!paginator?.hasNextPage) {
      return;
    }

    setLoadingEarlier(true);

    try {
      const nextPaginator = await paginator.nextPage();

      setPaginator(nextPaginator);

      setMessages((currentMessages) => [
        ...currentMessages,
        ...mergeChatMessages(nextPaginator.items)
          .map(mapMessage)
          .filter((message): message is ChatMessageViewModel =>
            Boolean(message),
          ),
      ]);
    } catch {
      Toast.showErrorToast({
        text1: 'Could not load earlier messages',
        text2: 'Please try again.',
      });
    } finally {
      setLoadingEarlier(false);
    }
  }, [mapMessage, paginator]);

  const handleSendMessage = useCallback(
    async (nextMessage: string) => {
      if (!conversation || !nextMessage || !onSendMessage) {
        return;
      }

      setSending(true);

      try {
        await onSendMessage(nextMessage);
      } catch {
        Toast.showErrorToast({
          text1: 'Could not send message',
          text2: 'Please try again.',
        });
      } finally {
        setSending(false);
      }
    },
    [conversation, onSendMessage],
  );

  return (
    <YStack flex={1}>
      <ChatConversationPanel
        activeConversationUniqueName={
          conversation?.uniqueName ?? conversation?.sid
        }
        activeUserId={activeUserId}
        emptyMessage={emptyMessage}
        hideSenderIdentity={hideSenderIdentity}
        inlineTitle={inlineTitle}
        isLoading={loading}
        isLoadingEarlier={loadingEarlier}
        isLocked={lockChat}
        isSending={sending}
        loadEarlier={paginator?.hasNextPage ?? false}
        messages={displayMessages}
        onLoadEarlier={onLoadEarlier}
        onSendMessage={handleSendMessage}
        placeholder={placeholder}
        readOnly={readOnly}
        receivedBubbleBackground={receivedBubbleBackground}
        showEmptyState={!conversation}
      />
    </YStack>
  );
};

export { ConversationChatView };
